import {useEffect, useState} from 'react';

import {
  AfriexAdminPermission,
  MarketPlaceTradeStatus as TradeStatus,
} from '@afriex/core/domain/enums';

import {Button, TableSkeleton} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import {merchantService} from '@/services/merchants';
import type {
  MarketPlaceAsk,
  MarketPlaceTrade,
  MarketPlaceTradeSummary,
} from '@/types/MarketPlace';

import DownloadTradesCsvButton from './DownloadTradesCsvButton';
import {EMPTY_TRADE_SUMMARY} from './useMarketPlaceTrades';
import {
  formatAmount,
  formatCount,
  getFilledPercentage,
  getTradeDate,
  getTradePair,
  getTradeStatusClassName,
  getTradeStatusLabel,
} from './utils/format';

type OfferTradesSectionProps = {
  ask: MarketPlaceAsk;
  onViewAllTrades: (askId: number) => void;
};

const OFFER_TRADE_PREVIEW_LIMIT = 5;

const Metric = ({label, value}: {label: string; value: string}) => (
  <div className="flex flex-col">
    <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
      {label}
    </span>
    <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
      {value}
    </span>
  </div>
);

const OfferTradesSection = ({
  ask,
  onViewAllTrades,
}: OfferTradesSectionProps) => {
  const [trades, setTrades] = useState<MarketPlaceTrade[]>([]);
  const [summary, setSummary] =
    useState<MarketPlaceTradeSummary>(EMPTY_TRADE_SUMMARY);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  // Each offer row renders its own section, so `ask.id` never changes under a
  // mounted one and the initial state is the loading state.
  useEffect(() => {
    const controller = new AbortController();
    merchantService
      .getMarketPlaceTrades(
        {askId: ask.id, limit: OFFER_TRADE_PREVIEW_LIMIT},
        controller.signal,
      )
      .then((result) => {
        // Aborted on unmount: nothing is left to update.
        if (controller.signal.aborted) {
          return;
        }
        setTrades(result.data);
        setSummary(result.summary ?? EMPTY_TRADE_SUMMARY);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        setError(requestError);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => controller.abort();
  }, [ask.id]);

  const filledPercentage = getFilledPercentage(ask.volume, ask.filledVolume);
  const settledPair = summary.settledByPair[0];

  const renderTradeList = () => {
    if (error !== null) {
      return (
        <p className="text-sm text-red-600 dark:text-red-400">
          Could not load trades for this offer.
        </p>
      );
    }
    if (trades.length === 0) {
      return (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No trades on this offer yet
        </p>
      );
    }
    return (
      <ul className="divide-y divide-gray-200 dark:divide-dark-borderMid">
        {trades.map((trade) => (
          <li
            key={trade.id}
            className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
          >
            <span className="font-medium text-gray-800 dark:text-gray-100">
              #{trade.id} {getTradePair(trade)}
            </span>
            <span className="text-gray-600 dark:text-gray-300">
              {trade.businessName || trade.businessId}
            </span>
            <span className="text-gray-600 dark:text-gray-300">
              {formatAmount(trade.volume)} &rarr;{' '}
              {formatAmount(trade.counterAmount)}
            </span>
            <span className={getTradeStatusClassName(trade.status)}>
              {getTradeStatusLabel(trade.status)}
            </span>
            <span className="text-gray-500 dark:text-gray-400">
              {getTradeDate(trade.createdAt)}
            </span>
          </li>
        ))}
      </ul>
    );
  };

  if (isLoading) {
    return <TableSkeleton />;
  }

  // A failed read leaves the empty summary, whose zeros would read as "no
  // trades"; the offer's own numbers still show.
  const hasSummary = error === null;

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
            Trades on this offer
          </h3>
          {hasSummary && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {formatCount(summary.tradeCount)} trades &middot;{' '}
              {formatCount(summary.statusCounts[TradeStatus.SETTLING] ?? 0)}{' '}
              settling &middot;{' '}
              {formatCount(summary.statusCounts[TradeStatus.EXPIRED] ?? 0)}{' '}
              expired
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <PermissionsProvider
            permission={AfriexAdminPermission.MarketPlaceRead}
          >
            <DownloadTradesCsvButton filters={{askId: ask.id}} />
          </PermissionsProvider>
          <Button
            variant="outline"
            colorScheme="cyan"
            onClick={() => onViewAllTrades(ask.id)}
          >
            View all trades
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <Metric
          label="Filled (paid)"
          value={`${formatAmount(ask.filledVolume)} / ${formatAmount(ask.volume)}`}
        />
        <Metric
          label="Unfilled (paid)"
          value={formatAmount(ask.unfilledVolume)}
        />
        <Metric
          label="Trades on offer"
          value={hasSummary ? formatCount(summary.tradeCount) : '-'}
        />
        {settledPair && (
          <Metric
            label="Counter amount paid"
            value={formatAmount(settledPair.counterAmount)}
          />
        )}
        <Metric
          label="Unique counterparties"
          value={hasSummary ? formatCount(summary.counterpartyCount) : '-'}
        />
      </div>

      <div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
          <div
            className="h-full rounded-full bg-cyan-600"
            style={{width: `${filledPercentage}%`}}
          />
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {filledPercentage}% filled by paid trades
        </p>
      </div>

      {renderTradeList()}
    </div>
  );
};

export default OfferTradesSection;
